import React, { useEffect, useState, useCallback } from "react";
import api from "../lib/api";
import { Button } from "./ui/button";
import { toast } from "sonner";
import {
  Snowflake, CheckCircle2, Circle, Calendar, Clock, AlertTriangle, Pencil, Trash2,
  Video, FileCheck, ChevronRight, Sparkles, Trophy, Flame,
} from "lucide-react";
import { useAuth } from "../lib/auth";
import EngagementEditorDialog, { FreezeDialog } from "./EngagementEditorDialog";
import TaskExplorerDialog from "./TaskExplorerDialog";
import CreateEngagementDialog from "./CreateEngagementDialog";

function fmtDate(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString(undefined, { day: "numeric", month: "short" }); } catch { return iso; }
}
function fmtDayName(iso) {
  if (!iso) return "";
  try { return new Date(iso).toLocaleDateString(undefined, { weekday: "short" }); } catch { return ""; }
}
function fmtINR(n) { return n == null ? "—" : `₹${Number(n).toLocaleString("en-IN", { maximumFractionDigits: 0 })}`; }

export default function EngagementProgress({ clientId }) {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [engFull, setEngFull] = useState(null);   // full doc for TaskExplorer
  const [banners, setBanners] = useState([]);
  const [err, setErr] = useState("");
  const [editorOpen, setEditorOpen] = useState(false);
  const [freezeOpen, setFreezeOpen] = useState(false);
  const [explorerOpen, setExplorerOpen] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const isAdmin = user?.role === "admin";
  const isTeam = user?.role === "admin" || user?.role === "team" || user?.role === "account_manager";

  const load = useCallback(async () => {
    try {
      const engs = (await api.get(`/engagements`, { params: { client_id: clientId } })).data;
      if (!engs || engs.length === 0) { setData({ empty: true }); return; }
      const eng = engs[0];
      const [prog, full] = await Promise.all([
        api.get(`/engagements/${eng.id}/progress`),
        api.get(`/engagements/${eng.id}`),
      ]);
      setData(prog.data);
      setEngFull(full.data);
      try { setBanners((await api.get(`/engagements/${eng.id}/reminders`)).data); } catch { setBanners([]); }
      setErr("");
    } catch (e) {
      setErr(e?.response?.data?.detail || "Failed to load progress");
    }
  }, [clientId]);

  useEffect(() => { load(); }, [load]);

  if (!data && !err) return <div className="text-zinc-500 text-[12px]">Loading progress…</div>;
  if (err) return <div className="text-red-400 text-[12px]">{err}</div>;
  if (data?.empty) {
    return isAdmin ? (
      <>
        <div className="tt-card p-4 text-[12px] text-zinc-500 flex items-center justify-between" data-testid="engagement-empty">
          <span>No engagement yet.</span>
          <Button size="sm" onClick={() => setCreateOpen(true)}
            data-testid="create-engagement-btn"
            className="bg-white text-black hover:bg-zinc-200 h-7 text-[11px]">
            New engagement →
          </Button>
        </div>
        <CreateEngagementDialog open={createOpen} onOpenChange={setCreateOpen}
          clientId={clientId} onCreated={load} />
      </>
    ) : null;
  }
  const eng = data.engagement;
  const totals = data.totals;

  async function createDefault() {
    try {
      const tpls = (await api.get("/engagement-templates")).data;
      const tpl = tpls.find((t) => t.id === "rtso-45") || tpls[0];
      const eventDate = new Date(Date.now() + 45 * 86400000).toISOString().slice(0, 10);
      await api.post("/engagements", { client_id: clientId, template_id: tpl.id, event_date: eventDate, length_days: tpl.length_days });
      toast.success("Engagement created");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Create failed"); }
  }

  // Freeze full-takeover for client-role users
  if (eng.frozen && user?.role === "client") {
    return (
      <div className="tt-card p-6 border-amber-500/40 bg-amber-500/5" data-testid="freeze-banner">
        <div className="flex items-center gap-2 mb-3">
          <Snowflake className="w-4 h-4 text-amber-400" />
          <div className="t-eyebrow text-amber-400">Dashboard paused</div>
        </div>
        <p className="text-[13px] text-zinc-200 whitespace-pre-line">{eng.freeze_message}</p>
      </div>
    );
  }

  const tickDeliverable = async (id, done) => {
    if (!isTeam) return;
    try { await api.post(`/engagements/${eng.id}/deliverables/${id}/tick`, { done }); load(); }
    catch { toast.error("Tick failed"); }
  };
  const tickMeeting = async (id, done) => {
    if (!isTeam) return;
    try { await api.post(`/engagements/${eng.id}/meetings/${id}/tick`, { done }); load(); }
    catch { toast.error("Tick failed"); }
  };
  const ackBanner = async (b) => {
    try { await api.post(`/engagements/${eng.id}/reminders/ack`, { milestone_id: b.milestone_id, banner_key: b.banner_key }); load(); }
    catch { toast.error("Ack failed"); }
  };
  const toggleFreeze = async () => {
    if (!isAdmin) return;
    if (eng.frozen) {
      await api.post(`/engagements/${eng.id}/unfreeze`); toast.success("Unfrozen"); load();
      return;
    }
    setFreezeOpen(true);
  };

  const overall = totals.overall_pct;
  const nextMeeting = (data.upcoming_meetings || [])[0];
  const upcomingDeliverables = (data.upcoming_deliverables || []).slice(0, 3);

  // Payment reminders are now surfaced from the per-client payments[] via <PaymentDueReminders>
  // on the client dashboard — this block no longer renders payment banners/popups.
  const paymentDueSoon = [];

  return (
    <div className="space-y-4" data-testid="engagement-progress">
      {/* Freeze badge (admin/team view) */}
      {eng.frozen && (
        <div className="tt-card p-3 border-amber-500/40 bg-amber-500/5 flex items-center justify-between" data-testid="freeze-badge">
          <div className="flex items-center gap-2">
            <Snowflake className="w-3.5 h-3.5 text-amber-400" />
            <span className="text-[12px] text-amber-200">Frozen · {eng.freeze_reason}</span>
          </div>
          {isAdmin && <Button size="sm" onClick={toggleFreeze} className="h-7 text-[11px]" data-testid="unfreeze-btn">Unfreeze</Button>}
        </div>
      )}

      {/* Reminder banners */}
      {banners.map((b) => (
        <div key={`${b.milestone_id}-${b.banner_key}`}
             data-testid={`reminder-banner-${b.banner_key}`}
             className={`tt-card p-3 flex items-center justify-between ${b.banner_key === "overdue" ? "border-[#EF4444]/40 bg-[#EF4444]/5" : "border-amber-500/40 bg-amber-500/5"}`}>
          <div className="flex items-center gap-2 text-[12px]">
            {b.banner_key === "overdue" ? <AlertTriangle className="w-3.5 h-3.5 text-[#EF4444]" /> : <Clock className="w-3.5 h-3.5 text-amber-400" />}
            <span className="text-zinc-200">
              {b.label} · {b.banner_key === "overdue" ? "overdue since" : `${b.banner_key} · due`} {fmtDate(b.due_date)}
              {b.amount != null && <span className="text-zinc-500 ml-2">{fmtINR(b.amount)}</span>}
            </span>
          </div>
          <Button size="sm" variant="ghost" onClick={() => ackBanner(b)} data-testid={`reminder-ack-${b.banner_key}`} className="h-7 text-[11px]">
            I acknowledge
          </Button>
        </div>
      ))}

      {/* Payment due-soon reminders (<5 days away, not covered by server banners) */}
      {paymentDueSoon.map((m) => (
        <div key={`due-soon-${m.id}`}
             data-testid={`payment-due-soon-${m.id}`}
             className="tt-card p-3 flex items-center justify-between border-amber-500/40 bg-amber-500/5">
          <div className="flex items-center gap-2 text-[12px]">
            <Clock className="w-3.5 h-3.5 text-amber-400" />
            <span className="text-zinc-200">
              Payment "{m.label}" due {m._daysUntil === 0 ? "today" : m._daysUntil === 1 ? "tomorrow" : `in ${m._daysUntil} days`} · {fmtDate(m.due_date)}
              {m.amount != null && <span className="text-zinc-500 ml-2">{fmtINR(m.amount)}</span>}
            </span>
          </div>
        </div>
      ))}


      {/* Big gamified progress hero */}
      <div className="tt-card p-5 relative overflow-hidden" data-testid="progress-hero">
        {/* subtle radial glow */}
        <div className="absolute inset-0 bg-gradient-to-br from-emerald-500/[0.03] via-transparent to-sky-500/[0.05] pointer-events-none" />
        <div className="relative flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-4">
          <div>
            <div className="t-eyebrow flex items-center gap-2">
              <span className="w-1 h-3 rounded bg-gradient-to-b from-sky-400 to-emerald-500" />
              Progress · {eng.name}
            </div>
            <div className="mt-1 flex items-baseline gap-2">
              <span className="text-zinc-100 text-[14px] font-display font-semibold">
                {eng.current_phase ? eng.current_phase.name : "—"}
              </span>
              {overall >= 100 && (
                <span className="text-[10px] uppercase tracking-wider text-emerald-300 bg-emerald-950/60 border border-emerald-800 rounded px-2 py-0.5 font-mono flex items-center gap-1">
                  <Trophy className="w-2.5 h-2.5" /> Delivered
                </span>
              )}
            </div>
          </div>
          <div className="flex items-center gap-1.5 flex-wrap">
            <Button size="sm" variant="ghost" onClick={() => setExplorerOpen(true)}
              data-testid="view-all-tasks-btn"
              className="h-7 text-[11px] text-zinc-300 border border-zinc-800 hover:border-zinc-600">
              View all tasks <ChevronRight className="w-3 h-3 ml-1" />
            </Button>
            {isAdmin && <Button size="sm" variant="ghost" onClick={() => setEditorOpen(true)} data-testid="edit-engagement-btn" className="h-7 text-[11px]">
              <Pencil className="w-3 h-3 mr-1" />Edit
            </Button>}
            {isAdmin && <Button size="sm" variant="ghost" onClick={toggleFreeze} data-testid="freeze-btn" className="h-7 text-[11px]">
              <Snowflake className="w-3 h-3 mr-1" />{eng.frozen ? "Unfreeze" : "Freeze"}
            </Button>}
            {isAdmin && <Button size="sm" variant="ghost" onClick={async () => {
              if (!window.confirm("Delete this engagement and all its phases, deliverables, meetings and time tracking? This cannot be undone. Payments are kept separately.")) return;
              try {
                await api.delete(`/engagements/${eng.id}`);
                toast.success("Engagement deleted");
                load();
              } catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
            }} data-testid="delete-engagement-btn" className="h-7 text-[11px] text-[#EF4444] hover:text-[#F87171]">
              <Trash2 className="w-3 h-3 mr-1" />Delete
            </Button>}
          </div>
        </div>

        {/* Big progress bar */}
        <div className="relative">
          <div className="h-3 rounded-full bg-zinc-900 overflow-hidden ring-1 ring-zinc-800">
            <div
              className="h-full bg-gradient-to-r from-emerald-500 via-emerald-400 to-sky-400 transition-[width] duration-1000 ease-out relative"
              style={{ width: `${overall}%` }}
              data-testid="progress-hero-bar">
              {overall >= 100 && (
                <div className="absolute inset-0 flex items-center justify-end pr-2">
                  <Sparkles className="w-2.5 h-2.5 text-white/80" />
                </div>
              )}
            </div>
          </div>
          <div className="mt-2 flex flex-wrap items-center justify-between text-[11px] font-mono text-zinc-500 tabular-nums gap-2">
            <span data-testid="progress-overall">
              <span className="text-zinc-200">{overall}%</span> · {totals.deliverables.done + totals.meetings.done}/{totals.deliverables.total + totals.meetings.total} tasks
            </span>
            <span className="flex items-center gap-3">
              <span data-testid="track-deliverables">
                <FileCheck className="w-2.5 h-2.5 inline mr-1 -mt-0.5" />
                {totals.deliverables.done}/{totals.deliverables.total}
              </span>
              <span data-testid="track-meetings">
                <Video className="w-2.5 h-2.5 inline mr-1 -mt-0.5" />
                {totals.meetings.done}/{totals.meetings.total}
              </span>
            </span>
          </div>
        </div>
      </div>

      {/* Next meeting — full-width dedicated card */}
      <div className="tt-card p-4" data-testid="next-meeting-card">
        <div className="flex items-center justify-between mb-2">
          <div className="t-eyebrow flex items-center gap-1.5">
            <Video className="w-3 h-3 text-sky-400" /> Next meeting
          </div>
          <button onClick={() => setExplorerOpen(true)} data-testid="view-more-meetings"
            className="text-[10px] uppercase tracking-wider text-zinc-500 hover:text-zinc-200 font-mono transition-colors">
            View more →
          </button>
        </div>
        {nextMeeting ? (
          <div className="flex items-center gap-3 py-1" data-testid={`next-meeting-${nextMeeting.id}`}>
            {isTeam ? (
              <button onClick={() => tickMeeting(nextMeeting.id, true)}
                data-testid={`tick-${nextMeeting.id}`}
                className="w-5 h-5 rounded-full border border-zinc-700 hover:border-emerald-400 flex items-center justify-center text-zinc-500 hover:text-emerald-400 transition-colors">
                <Circle className="w-2.5 h-2.5" />
              </button>
            ) : (
              <Circle className="w-4 h-4 text-zinc-600" />
            )}
            <div className="flex-1 min-w-0">
              <div className="text-zinc-100 text-[13.5px] truncate">{nextMeeting.title}</div>
              <div className="text-[11px] text-zinc-500 font-mono">
                {fmtDayName(nextMeeting.due_date)} · {fmtDate(nextMeeting.due_date)}
              </div>
            </div>
          </div>
        ) : (
          <div className="text-[11px] text-zinc-600">No meetings scheduled</div>
        )}
      </div>

      {/* Upcoming deliverables — dedicated card */}
      <div className="tt-card p-4" data-testid="upcoming-deliverables">
        <div className="flex items-center justify-between mb-2">
          <div className="t-eyebrow flex items-center gap-1.5">
            <FileCheck className="w-3 h-3 text-zinc-400" /> Upcoming deliverables
          </div>
          <button onClick={() => setExplorerOpen(true)} data-testid="view-more-deliverables"
            className="text-[10px] uppercase tracking-wider text-zinc-500 hover:text-zinc-200 font-mono transition-colors">
            View all →
          </button>
        </div>
        {upcomingDeliverables.length === 0 ? (
          <div className="text-[11px] text-zinc-600">Nothing due soon</div>
        ) : (
          <div className="space-y-1.5">
            {upcomingDeliverables.map((it) => (
              <div key={it.id} className="flex items-center gap-2.5 py-1 text-[12.5px]"
                data-testid={`upcoming-${it.id}`}>
                {isTeam ? (
                  <button onClick={() => tickDeliverable(it.id, true)}
                    data-testid={`tick-${it.id}`}
                    className="w-4 h-4 rounded-full border border-zinc-700 hover:border-emerald-400 flex items-center justify-center text-zinc-500 hover:text-emerald-400 transition-colors">
                    <Circle className="w-2 h-2" />
                  </button>
                ) : (
                  <Circle className="w-3.5 h-3.5 text-zinc-600" />
                )}
                <span className="flex-1 truncate text-zinc-100">{it.title}</span>
                {(it.iterations || 0) > 0 && (
                  <span className="text-[9px] font-mono text-amber-300 bg-amber-950/40 border border-amber-900/50 rounded px-1.5 py-0.5 flex items-center gap-1">
                    <Flame className="w-2 h-2" /> v{it.iterations}
                  </span>
                )}
                <span className="text-zinc-500 font-mono text-[11px]">
                  <Calendar className="w-3 h-3 inline mr-1" />{fmtDate(it.due_date)}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>



      <TaskExplorerDialog
        open={explorerOpen}
        onOpenChange={setExplorerOpen}
        engagement={engFull}
        onChanged={load} />

      {isAdmin && (
        <>
          <EngagementEditorDialog
            mode="engagement" entityId={eng.id}
            open={editorOpen} onOpenChange={setEditorOpen} onSaved={load} />
          <FreezeDialog
            open={freezeOpen} onOpenChange={setFreezeOpen}
            engagement={eng} milestones={eng.payment_milestones}
            onFrozen={load} />
        </>
      )}
    </div>
  );
}
